import { useEffect, useState } from "react";
import { BellRing, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders } from "../lib/supabase";
import { pushSupport, permission, currentSubscription, subscribePush, unsubscribePush } from "../lib/push";

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between py-2">
    <span className="text-sm">{label}</span>
    <input type="checkbox" className="toggle" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
  </label>
);

export const NotificationsCard = () => {
  const support = pushSupport();
  const [perm, setPerm] = useState(permission());
  const [sub, setSub] = useState(null);
  const [devices, setDevices] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    setSub(await currentSubscription().catch(() => null));
    try {
      const [d, p] = await Promise.all([fetch(`${API}/push/devices`, { headers: await authHeaders() }), fetch(`${API}/notifications/prefs`, { headers: await authHeaders() })]);
      if (d.ok) setDevices((await d.json()).devices);
      if (p.ok) setPrefs(await p.json());
    } catch { /* offline */ }
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy("enable");
    const r = await subscribePush().catch(() => ({ ok: false, reason: "server_error" }));
    setBusy(""); setPerm(permission());
    if (r.ok) { toast.success("Alerts enabled on this device"); load(); }
    else toast.error({ blocked: "Notifications are blocked for this site — allow them in your browser's site settings", dismissed: "Permission not granted", server_disabled: "Alerts aren't available yet", server_error: "Could not enable — try again" }[r.reason] || "Could not enable");
  };
  const disable = async () => { setBusy("disable"); const r = await unsubscribePush().catch(() => ({ ok: false })); setBusy(""); if (r.ok) { toast.success("Alerts turned off on this device"); load(); } else toast.error("Could not turn off — try again"); };
  const savePref = async (k, v) => {
    const next = { ...prefs, [k]: v }; setPrefs(next);
    try { const r = await fetch(`${API}/notifications/prefs`, { method: "PUT", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify({ [k]: v }) }); if (!r.ok) throw new Error(); }
    catch { toast.error("Could not save preference"); setPrefs(prefs); }
  };
  const test = async () => {
    setBusy("test");
    try { const r = await fetch(`${API}/notifications/test`, { method: "POST", headers: await authHeaders() }); const b = await r.json(); if (!r.ok) throw new Error(b.detail || "failed"); toast.success(`Test alert sent to ${b.sent} device${b.sent === 1 ? "" : "s"}${b.failed ? ` (${b.failed} failed)` : ""}`); }
    catch (e) { toast.error(e.message === "notifications_unavailable" ? "Alerts aren't available yet" : "Test alert failed"); }
    setBusy("");
  };

  const status = !support.ok ? (support.reason === "ios_install" ? "Not available in Safari tabs — add DropIt to your Home Screen first, then enable here" : "Not supported in this browser")
    : perm === "denied" ? "Blocked — allow notifications for this site in your browser settings, then try again"
    : sub ? "Enabled on this device" : "Not enabled on this device";
  return (
    <section className="card fade-up" data-testid="settings-notifications-card">
      <p className="eyebrow">Notifications</p>
      <p className="text-sm text-muted mt-1">Get alerted the moment someone claims, completes, or sends proof for a job — even when the app is closed.</p>
      <p className="text-sm mt-3 flex items-center gap-2" data-testid="notifications-status">{sub ? <BellRing size={16} className="text-primary" /> : <BellOff size={16} className="text-muted" />} {status}</p>
      {support.brave && !sub && <p className="text-xs text-muted mt-1" data-testid="brave-push-hint">Brave: turn on “Use Google services for push messaging” in brave://settings/privacy</p>}
      {devices != null && <p className="text-xs text-muted mt-1 notranslate" translate="no" data-testid="notifications-devices">{devices} device{devices === 1 ? "" : "s"} subscribed</p>}
      {support.ok && perm !== "denied" && (
        sub ? <button type="button" className="btn-secondary mt-3" onClick={disable} disabled={!!busy} data-testid="notifications-disable-button">{busy === "disable" ? <Loader2 className="animate-spin inline" size={16} /> : "Turn off on this device"}</button>
            : <button type="button" className="btn-primary mt-3" onClick={enable} disabled={!!busy} data-testid="notifications-enable-button">{busy === "enable" ? <Loader2 className="animate-spin inline" size={16} /> : "Enable on this device"}</button>
      )}
      {prefs && !prefs.unavailable && (
        <div className="mt-4 border-t border-[var(--line)] pt-2" data-testid="notifications-prefs">
          <Toggle label="Someone claims a job" checked={prefs.notify_claimed} onChange={(v) => savePref("notify_claimed", v)} testid="pref-claimed" />
          <Toggle label="Job completed" checked={prefs.notify_completed} onChange={(v) => savePref("notify_completed", v)} testid="pref-completed" />
          <Toggle label="Proof photos submitted" checked={prefs.notify_proof} onChange={(v) => savePref("notify_proof", v)} testid="pref-proof" />
        </div>
      )}
      <button type="button" className="btn-secondary mt-3 flex items-center justify-center gap-2" onClick={test} disabled={!!busy} data-testid="notifications-test-button"><Send size={14} /> {busy === "test" ? "Sending…" : "Send test alert"}</button>
    </section>
  );
};
